/**
 * Update the dropdown options for a char filter.
 *
 * @param {Object} originalFilter - The backup filter object.
 * @param {Object} attr - The current filter attribute.
 * @param {Array} filteredProducts - Array of filtered product objects.
 * @returns {Array} - The filtered list of values.
 */
export function updateCharFilterOptions(originalFilter, attr, filteredProducts) {
  const valuesSet = new Set();

  filteredProducts.forEach(product => {
    // Check if the attribute exists in the product.attributes array
    const productAttr = product.attributes?.find(a => a.title === attr.title);
    if (productAttr && productAttr.value) {
      valuesSet.add(productAttr.value);
    }
    // Check if the attribute exists as a direct property on the product
    if (product[attr.name]) {
      valuesSet.add(product[attr.name]);
    }
  });

  // Return only the original values that are available in the filtered products
  return originalFilter.values.filter(value => valuesSet.has(value));
}

/**
 * Determine the numeric range for a number filter.
 *
 * @param {Object} originalFilter - The backup filter object.
 * @param {Object} attr - The current filter attribute.
 * @param {Array} filteredProducts - Array of filtered product objects.
 * @returns {Array} - The numeric range as an array: [min, max].
 */
export function updateNumberFilterRange(originalFilter, attr, filteredProducts) {
  let minVal = Infinity;
  let maxVal = -Infinity;
  let hasValue = false;

  filteredProducts.forEach(product => {
    let value;
    // Try to get the value from product.attributes first
    const productAttr = product.attributes?.find(a => a.title === attr.title);
    if (productAttr && productAttr.value !== undefined) {
      value = Number(productAttr.value);
    }
    // Otherwise, check the product's direct property
    else if (product[attr.name] !== undefined) {
      value = Number(product[attr.name]);
    }

    if (value !== undefined && !isNaN(value)) {
      minVal = Math.min(minVal, value);
      maxVal = Math.max(maxVal, value);
      hasValue = true;
    }
  });

  // If at least one valid number was found, return the computed range;
  // otherwise, fall back to the original filter range.
  return hasValue
    ? [minVal, maxVal]
    : [originalFilter.range.min, originalFilter.range.max];
}
